import type { DensityOverrideRange } from '~/domain/patient/roi';
import {
    AmbiguousDecimalReadings,
    ambiguousDecimalReadings,
    normalizeDecimalSeparator,
} from '~/utils/decimalInput';
import { DENSITY_OVERRIDE_DECIMALS } from '~/utils/units';

/**
 * Interpreting a density-override edit: the user can type a value to set it, or
 * clear the field to remove the override entirely (which omits the physical
 * property from the output RTSTRUCT). Kept pure so the parse/validation rules
 * are unit-testable without rendering the field.
 */
export type DensityInput =
    | { readonly kind: 'remove' }
    | { readonly kind: 'set'; readonly value: number }
    | {
          readonly kind: 'ambiguous';
          readonly readings: AmbiguousDecimalReadings;
      }
    | { readonly kind: 'invalid' };

/**
 * Parse a density-override input string against an accepted `[min, max]` range
 * (see {@link DENSITY_OVERRIDE_RANGE}).
 *
 * - Empty (or whitespace-only) → `remove`: clear the override so nothing is
 *   written to the output RTSTRUCT.
 * - A finite number within the inclusive range → `set`: relative electron/mass
 *   density is a dimensionless ratio; a boundary value (e.g. exactly `range.min`
 *   or `range.max`) is legitimate and preserved. The value is rounded to
 *   {@link DENSITY_OVERRIDE_DECIMALS} so the committed number is exactly what
 *   the field displays and what is exported — the clinician never sees one
 *   value and stores another.
 * - Anything else (non-numeric, non-finite, or out of range) → `invalid`:
 *   surfaced as an error rather than silently clamped or coerced.
 *
 * A point or a comma is the decimal separator, as in every numeric field
 * (see {@link normalizeDecimalSeparator}); an entry with more than one
 * separator is `invalid`. An entry that may also be a whole number written
 * with a thousands separator (`1,500`) is `ambiguous` when the range holds
 * the whole number (see {@link ambiguousDecimalReadings}); a range that ends
 * below 1000 never does, so there `1,500` is 1.5.
 *
 * The range is passed in rather than read here, so the rule stays pure and
 * unit-testable.
 */
export function parseDensityInput(
    text: string,
    range: DensityOverrideRange
): DensityInput {
    if (text.trim() === '') {
        return { kind: 'remove' };
    }
    const trimmed = normalizeDecimalSeparator(text.trim());
    if (trimmed === null) {
        return { kind: 'invalid' };
    }
    // Check the decimal syntax BEFORE converting: `Number()` also accepts
    // JavaScript-only literal forms (`0x10` → 16, `0b10` → 2, `0o17` → 15) and
    // `Infinity`, so a nonsensical entry could otherwise be coerced into a
    // plausible in-range density and silently stored. A density override is
    // exported as a DICOM Decimal String (DS, PS3.5 §6.2), whose grammar is an
    // optional sign, decimal digits with an optional point, and an optional
    // exponent — exactly what this pattern allows.
    if (!DICOM_DECIMAL_STRING.test(trimmed)) {
        return { kind: 'invalid' };
    }
    // Bound the raw entry to the DICOM DS limit (16 bytes, PS3.5 §6.2) BEFORE
    // the lossy `Number()` conversion. This also closes a range-masking gap:
    // an entry within half a ULP above the maximum collapses onto it under
    // `Number()` (e.g. `Number('20.000000000000001') === 20`), and reaching that
    // half-ULP window takes ~17 significant digits — always more than 16
    // characters — so the length bound rejects every such collapse while never
    // touching a legitimate, short density entry (which is then rounded to
    // {@link DENSITY_OVERRIDE_DECIMALS}).
    if (trimmed.length > DICOM_DS_MAX_LENGTH) {
        return { kind: 'invalid' };
    }
    const value = Number(trimmed);
    // The remaining collapse is at the low end: a negative magnitude small
    // enough to underflow reads as `-0` (e.g. `Number('-1e-400')`), which would
    // slip past `value < range.min` since `-0 >= 0`. A negative entry must be
    // rejected, not silently accepted as zero. A bare signed zero (`-0`, `-0.0`)
    // carries no nonzero digit and stays a legitimate zero.
    if (Object.is(value, -0) && /[1-9]/.test(trimmed)) {
        return { kind: 'invalid' };
    }
    const isInRange = (candidate: number) =>
        candidate >= range.min && candidate <= range.max;
    // Read on the same text the number is, without the surrounding whitespace
    // this rule ignores, so that an entry it accepts is also one it can call
    // ambiguous.
    const readings = ambiguousDecimalReadings(text.trim(), isInRange);
    if (readings !== null) {
        return { kind: 'ambiguous', readings };
    }
    if (!Number.isFinite(value) || !isInRange(value)) {
        return { kind: 'invalid' };
    }
    // Round to the displayed precision on commit so the stored/exported value
    // is exactly what the clinician sees. Without this, a high-precision entry
    // (e.g. `1.23456`) would render as `1.235` but persist and export as
    // `1.23456` — a display/DS divergence, and a traceability defect in a SaMD.
    return { kind: 'set', value: roundToDensityPrecision(value) };
}

/**
 * Round a density override to {@link DENSITY_OVERRIDE_DECIMALS}. Re-parsing the
 * fixed string yields a number whose shortest string form is that rounded
 * decimal, keeping the exported DICOM Decimal String short and conformant.
 */
function roundToDensityPrecision(value: number): number {
    return Number(value.toFixed(DENSITY_OVERRIDE_DECIMALS));
}

/**
 * Decimal syntax accepted for a density override, matching the DICOM Decimal
 * String (DS) value representation: an optional sign, digits with an optional
 * decimal point (`1`, `1.`, `1.05`, `.5`), and an optional base-10 exponent.
 */
const DICOM_DECIMAL_STRING = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/;

/** Maximum length of a DICOM Decimal String (DS) value, in bytes (PS3.5 §6.2). */
const DICOM_DS_MAX_LENGTH = 16;
